<template>
	<v-overlay
		:dark="false"
		:absolute="modal"
		:class="modal == false ? 'v-overlay-relative' : null"
		:value="showSizingChart"
	>
		<div
			class="input-modal sizing-modal"
			:class="includeLengths == false ? 'sizing-modal-mini' : null"
			:style="includeLengths ? `width: ${lengthModalWidthRem}rem; min-width: auto` : null"
			data-test="size-dialog"
			:data-test-style-code="styleCode"
		>
			<div class="input-modal-header">
				<div v-if="systemCategoryId && itemId" class="d-inline-flex align-center mr-auto">
					<SystemCategoryIcon :system-category-id="systemCategoryId" :inventory-item-id="itemId" />
					<span v-if="styleCode" class="sizing-chart-style-code">{{ styleCode }}</span>
				</div>
				<v-btn v-if="!enableInputs || readonly" icon @click="$emit('cancel')">
					<v-icon v-text="'mdi-close'"></v-icon>
				</v-btn>
				<template v-else>
					<v-btn outlined dense hide-details="auto" @click="$emit('cancel')"> Cancel </v-btn>
					<v-btn outlined dense hide-details="auto" @click="saveSizingChart()"> Save </v-btn>
				</template>
			</div>
			<transition name="alert-fade">
				<div v-if="inputError" class="sizing-chart-alert">
					You can't enter values greater than the item quantity!
				</div>
			</transition>
			<div class="input-modal-content">
				<div class="sizing-chart" :style="includeLengths ? `width: ${lengthGridWidthRem}rem; margin: 0 auto` : null">
					<div v-if="includeLengths" class="length-header">
						<div class="length-header-spacer"></div>
						<div class="length-header-label" :style="`width: ${computedLengths.length * lengthColumnRem}rem`">Length</div>
					</div>
					<!-- table with lengths -->
					<div v-if="includeLengths" class="flex-table" role="table-body">
						<div
							class="flex-table-row"
							role="row-group"
							style="position: sticky; top: 3rem; z-index: 11"
						>
							<div class="flex-table-cell" role="cell">Size</div>
							<div
								v-for="length in computedLengths"
								:key="length"
								class="flex-table-cell"
								:class="{ 'highlight-col': focusedLength === length }"
								role="cell"
							>
								{{ length !== "N/A" ? `${length}"` : length }}
							</div>
						</div>
						<template v-for="(size, i) in sizeData">
							<div
								v-if="size.showSize || isSizeInSelection(size.sizeId)"
								:key="i"
								class="flex-table-row"
								role="row-group"
							>
								<div class="flex-table-cell" :class="{ 'highlight-row': focusedSizeName === size.name }" role="cell">{{ size.name }}</div>
								<div
									v-for="(length, j) in computedLengths"
									:key="j"
									class="flex-table-cell"
									role="cell"
								>
									<input
										v-if="renderSizeOption(length, size)"
										@input="updateQuantityByLength(size, length, $event)"
										@keydown="onCellKeydown($event)"
										@focus="onCellFocus(size.name, length)"
										@blur="onCellBlur()"
										:value="enableInputs ? renderSizeSelectionByLength(size, length) : null"
										type="text"
										:disabled="!enableInputs || readonly"
										:class="isStandardLength(length, size) ? 'standard-length' : null"
									/>
								</div>
							</div>
						</template>
					</div>
					<!-- table with no lengths -->
					<div v-else class="flex-table-2" role="table-body">
						<div class="flex-table-row" role="row-group" style="position: sticky; top: 0; z-index: 10">
							<div class="flex-table-cell" role="cell">Size</div>
							<div class="flex-table-cell" role="cell">Quantity</div>
						</div>
						<template v-for="(size, i) in sizeData">
							<div
								v-if="size.showSize || isSizeInSelection(size.sizeId)"
								:key="i"
								class="flex-table-row"
								role="row-group"
							>
								<div class="flex-table-cell" role="cell">{{ size.name }}</div>
								<div class="flex-table-cell" role="cell">
									<input
										@input="updateQuantity(size, $event)"
										@keydown="onCellKeydown($event)"
										@focus="onCellFocus(size.name, null)"
										@blur="onCellBlur()"
										:value="enableInputs ? renderSizeSelection(size) : null"
										type="text"
										:disabled="!enableInputs || readonly"
									/>
								</div>
							</div>
						</template>
					</div>
				</div>
				<!-- special size inputs (scrolls with chart) -->
				<div v-if="enableInputs && specialSizes.length > 0" class="sizing-chart-special">
					<div class="sizing-chart-special-header">Special Sizes</div>
					<div
						class="flex-table"
						:class="includeLengths ? 'flex-table-3' : 'flex-table-4'"
						role="table-head"
					>
						<div class="flex-table-row" role="row-group">
							<div class="flex-table-cell" role="cell">Size</div>
							<div v-if="includeLengths" class="flex-table-cell" role="cell">Length</div>
							<div class="flex-table-cell" role="cell">Quantity</div>
							<div class="flex-table-cell" role="cell">&nbsp;</div>
						</div>
					</div>
					<div
						class="flex-table"
						:class="includeLengths ? 'flex-table-3' : 'flex-table-4'"
						role="table-body"
					>
						<template v-for="(data, i) in specialSizes">
							<div :key="i" class="flex-table-row" role="row-group" data-test="special-size-row">
								<div class="flex-table-cell" role="cell">
									<div class="form-input">
										<v-text-field
											outlined
											dense
											hide-details="auto"
											v-model="specialSizes[i].size"
											:disabled="readonly"
											:error="!!errorsSpecial[i]"
											:error-messages="errorsSpecial[i]"
											@input="validateSizeName"
										></v-text-field>
									</div>
								</div>
								<div v-if="includeLengths" class="flex-table-cell" role="cell">
									<div class="form-input">
										<v-text-field
											outlined
											dense
											hide-details="auto"
											v-model="specialSizes[i].length"
											:disabled="readonly"
											@input="validateSizeName"
										></v-text-field>
									</div>
								</div>
								<div class="flex-table-cell" role="cell">
									<div class="form-input">
										<v-text-field
											outlined
											dense
											hide-details="auto"
											:min="0"
											:max="5000"
											v-model.number="specialSizes[i].quantity"
											:disabled="readonly"
											type="number"
											@input="updateSpecialQuantity(i)"
										></v-text-field>
									</div>
								</div>
								<div class="flex-table-cell" role="cell">
									<div class="sizing-chart-special-row-action">
										<v-btn v-if="!readonly" icon @click="removeSpecialSize(data)">
											<v-icon>mdi-close</v-icon>
										</v-btn>
									</div>
								</div>
							</div>
						</template>
					</div>
				</div>
			</div>
			<!-- footer actions (always pinned at bottom) -->
			<div v-if="enableInputs && !readonly" class="input-modal-footer sizing-chart-footer">
				<div class="sizing-chart-footer-actions">
					<v-btn v-if="!stockItems" text color="accent" @click="addSpecialSize()">
						<v-icon>mdi-plus</v-icon>
						Special Size
					</v-btn>

					<div class="sizing-chart-total" :class="{ 'ml-auto': !stockItems }">
						<p>
							Total Quantity <span :class="totalClass">{{ computedTotal }}</span>
							<span class="total-required">of {{ maxQuantity }}</span>
						</p>
					</div>

					<div v-if="includeSpecialCharge" class="form-input">
						<label>Up-Charge Amount</label>
						<currency-text-field v-model="upChargeAmount" />
					</div>
				</div>
			</div>
		</div>
	</v-overlay>
</template>

<script>
import SystemCategoryIcon from "@/components/SystemCategoryIcon.vue";

export default {
	name: "SizingChart",
	props: {
		showSizingChart: {
			type: Boolean,
			default: false,
		},
		modal: {
			type: Boolean,
			default: true,
		},
		sizes: {
			type: Array,
			default() {
				return [];
			},
		},
		sizeSelectionData: {
			type: Array,
			default() {
				return [];
			},
		},
		specialSizing: {
			type: Array,
			default() {
				return [];
			},
		},
		includeSpecialCharge: {
			type: Boolean,
			default: true,
		},
		specialCharge: {
			type: Number,
		},
		includeLengths: {
			type: Boolean,
			default: false,
		},
		maxQuantity: {
			default: 0,
		},
		enableInputs: {
			type: Boolean,
			default: false,
		},
		// Show the current selections without allowing changes (locked quotes, #1475).
		// Unlike enableInputs=false (the inventory preview), the quantities stay visible.
		readonly: {
			type: Boolean,
			default: false,
		},
		stockItems: {
			type: Boolean,
			default: false,
		},
		itemId: {
			type: Number,
			default: null,
		},
		systemCategoryId: {
			type: Number,
			default: null,
		},
		styleCode: {
			type: String,
			default: null,
		},
	},
	components: {
		SystemCategoryIcon,
	},
	data() {
		return {
			sizeData: [
				{
					sizeId: null,
					name: "",
					lengths: "",
					showSize: null,
					quantity: null, //need to figure out data model for handling quantites by lengths
					isRetired: false,
				},
			],
			upChargeAmount: this.specialCharge,
			sizeSelections: [],
			specialSizes: [],
			startLength: 0,
			endLength: 1,
			inputError: false,
			errorsSpecial: [],
			focusedSizeName: null,
			focusedLength: null,
		};
	},
	methods: {
		isStandardLength(length, sizeObj) {
			let lengthString = this.enableInputs
				? sizeObj.lengths
						.filter((l) => !l.isRetired || this.isLengthInSelection(l.sizeLengthId))
						.map((len) => len.length)
						.toString()
				: sizeObj.lengths;
			let isStandard = false;
			let lengths = lengthString.split(",").map((l) => {
				var lenNumber = l.match(/\d+/g) ? parseInt(l.match(/\d+/g)[0]) : null;
				if (lenNumber === length && l.endsWith("*")) {
					isStandard = true;
				}
			});

			return isStandard;
		},
		renderSizeOption(length, sizeObj) {
			//get all the possible lengths of the passed in size
			let lengths;
			if (this.enableInputs) {
				//when inputs are enabled the size data model is slightly different so we need to map the each length obj instead of one string
				lengths = sizeObj.lengths
					.filter((l) => !l.isRetired || this.isLengthInSelection(l.sizeLengthId))
					.map((len) => len.length)
					.toString();
			} else {
				lengths = sizeObj.lengths;
			}

			//filter out any characters that are not numbers
			let filteredNumbers = lengths.match(/\d+/g);
			if (filteredNumbers && filteredNumbers.includes(length.toString())) {
				return true;
			} else {
				return length === "N/A" ? this.isSizeInSelection(sizeObj.sizeId) : false;
			}
		},

		addSpecialSize() {
			const specialSize = {
				size: "",
				length: "",
				quantity: 1,
			};

			this.specialSizes.push(specialSize);

			// The special sizes sit below the size grid inside the chart's scroll area, so a
			// new row is usually out of view. Bring it into view and focus its Size input.
			this.$nextTick(() => {
				const rows = this.$el.querySelectorAll('[data-test="special-size-row"]');
				const newRow = rows[rows.length - 1];
				if (!newRow) return;
				newRow.scrollIntoView({ behavior: "smooth", block: "nearest" });
				newRow.querySelector("input")?.focus({ preventScroll: true });
			});
		},

		removeSpecialSize(row) {
			this.specialSizes = this.specialSizes.filter((obj) => obj !== row);
		},

		updateQuantity(sizeObj, input) {
			let inputValue = input.target.value == "" ? 0 : parseInt(input.target.value);
			//check if the size already exists in the sizeSelection data
			let matchingSizeSelectionIndex = null;
			if (
				this.sizeSelections.length > 0 &&
				this.sizeSelections.some((obj) => obj.name == sizeObj.name)
			) {
				matchingSizeSelectionIndex = this.sizeSelections.findIndex(
					(obj) => obj.name == sizeObj.name
				);
				console.log("found match");
			}

			//if total quantity equals the line item quantity dont allow any more inputs
			let totalQuantity =
				matchingSizeSelectionIndex !== null
					? parseInt(this.computedTotal) -
					  parseInt(
							this.sizeSelections[matchingSizeSelectionIndex].quantity == ""
								? 0
								: this.sizeSelections[matchingSizeSelectionIndex].quantity
					  ) +
					  inputValue
					: parseInt(this.computedTotal) + inputValue;
			let fixedInputValue;

			if (totalQuantity > parseInt(this.maxQuantity)) {
				//change the user input value if it is greater than the maxQuantity allowed
				fixedInputValue = "";

				//display warning if user input is greater than maxQuantity
				this.inputError = true;
			} else {
				this.inputError = false;
				//allow single input of 0 but strip 0 if input is a 2 digit number or more
				fixedInputValue =
					input.target.value.length > 1
						? input.target.value.replace(/^0+/, "").replace(/[^\d.]/gi, "")
						: input.target.value.replace(/[^\d.]/gi, "");
			}

			//create a size selection input and update the sizeSelection Data
			let sizeInput = {
				sizeId: sizeObj.sizeId,
				name: sizeObj.name,
				quantity: fixedInputValue,
			};

			//check if the size already exists in the sizeSelection data
			if (matchingSizeSelectionIndex !== null) {
				this.$set(this.sizeSelections, matchingSizeSelectionIndex, sizeInput);
				console.log("replacing size selection");
			} else {
				console.log("adding size selection");
				this.sizeSelections.push(sizeInput);
			}
		},

		updateQuantityByLength(sizeObj, length, input) {
			let inputValue = input.target.value == "" ? 0 : parseInt(input.target.value);
			//check if the size and length already exists in the sizeSelection data
			let matchingSizeSelectionIndex = null;
			if (
				this.sizeSelections.length > 0 &&
				this.sizeSelections.some(
					(obj) => obj.name == sizeObj.name && obj.length.match(/\d+/g) == length
				)
			) {
				matchingSizeSelectionIndex = this.sizeSelections.findIndex(
					(obj) => obj.name == sizeObj.name && obj.length.match(/\d+/g) == length
				);
				console.log("found match");
			}

			//if total quantity equals the line item quantity dont allow any more inputs
			let totalQuantity =
				matchingSizeSelectionIndex !== null
					? parseInt(this.computedTotal) -
					  parseInt(
							this.sizeSelections[matchingSizeSelectionIndex].quantity == ""
								? 0
								: this.sizeSelections[matchingSizeSelectionIndex].quantity
					  ) +
					  inputValue
					: parseInt(this.computedTotal) + inputValue;
			let fixedInputValue;

			if (totalQuantity > parseInt(this.maxQuantity)) {
				//change the user input value if it is greater than the maxQuantity allowed
				fixedInputValue = "";

				//display warning if user input is greater than maxQuantity
				this.inputError = true;
			} else {
				this.inputError = false;
				//allow single input of 0 but strip leading 0 if input is a 2 digit number or more
				fixedInputValue =
					input.target.value.length > 1
						? input.target.value.replace(/^0+/, "").replace(/[^\d.]/gi, "")
						: input.target.value.replace(/[^\d.]/gi, "");
			}

			//create a size selection input and update the sizeSelection Data
			let sizeInput = {
				sizeId: sizeObj.lengths.find((obj) => obj.length.match(/\d+/g) == length).sizeId,
				name: sizeObj.name,
				quantity: fixedInputValue,
				length: sizeObj.lengths.find((obj) => obj.length.match(/\d+/g) == length).length,
				sizeLengthId: sizeObj.lengths.find((obj) => obj.length.match(/\d+/g) == length)
					.sizeLengthId,
			};

			//replace matching size selection or add new size selection
			if (matchingSizeSelectionIndex !== null) {
				this.$set(this.sizeSelections, matchingSizeSelectionIndex, sizeInput);
				console.log("replacing size selection");
			} else {
				console.log("adding size selection");
				this.sizeSelections.push(sizeInput);
			}
		},
		validateSizeName() {
			const sizeNames = [...this.specialSizes];
			const lastIndex = sizeNames.length - 1;
			const errors = [];
			sizeNames.reduce((acc, cur_, index) => {
				const cur = acc.pop();
				const idx = lastIndex - index;
				if (!cur) {
					errors[idx] = "Required";
				} else if (!!cur && acc.find((el) => el.size === cur.size && el.length === cur.length)) {
					errors[idx] = "Already exist";
				} else errors[idx] = "";
				return acc;
			}, sizeNames);
			this.errorsSpecial = errors;
			return !errors.some((el) => !!el);
		},
		updateSpecialQuantity(idx) {
			this.$nextTick(() => {
				if (this.maxQuantity < this.computedTotal) {
					this.inputError = true;
					this.specialSizes[idx].quantity = 0;
				} else {
					this.inputError = false;
				}
			});
		},

		renderSizeSelection(sizeObj) {
			//check if size selection data exists and render the quantity input
			let quantity = null;
			if (
				this.sizeSelections.length > 0 &&
				this.sizeSelections.some((obj) => obj.name == sizeObj.name && obj.sizeId == sizeObj.sizeId)
			) {
				let matchingSizeSelectionIndex = this.sizeSelections.findIndex(
					(obj) => obj.name == sizeObj.name && obj.sizeId == sizeObj.sizeId
				);
				quantity = this.sizeSelections[matchingSizeSelectionIndex].quantity;
			}

			return quantity;
		},

		renderSizeSelectionByLength(sizeObj, length) {
			//check if size selection data exists and render the quantity input
			let quantity = null;

			const onlyNumbers = (str) => parseFloat(str.match(/\d+/g));

			if (
				(this.sizeSelections.length > 0 &&
					this.sizeSelections.some(
						(obj) => obj.name == sizeObj.name && obj.length && obj.length.match(/\d+/g) == length
					)) ||
				this.sizeSelections.some(
					(obj) => obj.size == sizeObj.name && onlyNumbers(obj.length) == length
				)
			) {
				let matchingSizeSelectionIndex = this.sizeSelections.findIndex(
					(obj) => obj.name == sizeObj.name && obj.length && obj.length.match(/\d+/g) == length
				);

				if (matchingSizeSelectionIndex == -1) {
					matchingSizeSelectionIndex = this.sizeSelections.findIndex(
						(obj) => obj.size == sizeObj.name && onlyNumbers(obj.length) == length
					);
				}

				quantity = this.sizeSelections[matchingSizeSelectionIndex].quantity;
			} else if (length === "N/A") {
				quantity = this.renderSizeSelection(sizeObj);
			}

			return quantity;
		},

		saveSizingChart() {
			//send the updated sizeSelection data to the parent
			this.specialSizes = this.specialSizes.filter((el) => el.size || el.quantity);
			const valid = this.validateSizeName();
			if (!valid) return;
			// Validate total quantity (including special sizes) doesn't exceed line item quantity
			if (this.computedTotal > parseInt(this.maxQuantity)) {
				this.inputError = true;
				return;
			}
			let sizing = {
				sizeSelections: this.sizeSelections,
				specialSizeSelections: this.specialSizes,
				specialCharge: this.upChargeAmount,
				sizeChart: {
					sizes: this.sizes,
				},
			};

			console.log(this.sizes);

			this.$emit("save", sizing);
		},

		isSizeInSelection(sizeId) {
			return (
				this.sizeSelections &&
				this.sizeSelections.length > 0 &&
				this.sizeSelections.filter((s) => s.sizeId === sizeId) &&
				this.sizeSelections.filter((s) => s.sizeId === sizeId).length > 0
			);
		},

		isLengthInSelection(sizeLengthId) {
			return (
				this.sizeSelections &&
				this.sizeSelections.length > 0 &&
				this.sizeSelections.filter(
					(s) => s.sizeLengthId === sizeLengthId || s.lengthId === sizeLengthId
				) &&
				this.sizeSelections.filter(
					(s) => s.sizeLengthId === sizeLengthId || s.lengthId === sizeLengthId
				).length > 0
			);
		},
		onCellKeydown(event) {
			const keys = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Enter'];
			if (!keys.includes(event.key)) return;
			event.preventDefault();

			const tableBody = this.$el.querySelector(
				'.flex-table[role="table-body"], .flex-table-2[role="table-body"]'
			);
			if (!tableBody) return;

			const allInputs = Array.from(
				tableBody.querySelectorAll('input[type="text"]:not(:disabled)')
			);
			const currentIdx = allInputs.indexOf(event.target);
			if (currentIdx === -1) return;

			let targetInput;

			if (event.key === 'ArrowRight' || event.key === 'Enter') {
				targetInput = allInputs[currentIdx + 1];
			} else if (event.key === 'ArrowLeft') {
				targetInput = allInputs[currentIdx - 1];
			} else {
				const currentCell = event.target.closest('.flex-table-cell');
				const currentRow = event.target.closest('.flex-table-row');
				if (!currentCell || !currentRow) return;

				const cellsInRow = Array.from(currentRow.querySelectorAll('.flex-table-cell'));
				const colIdx = cellsInRow.indexOf(currentCell);

				const allRows = Array.from(tableBody.querySelectorAll('.flex-table-row'));
				const rowIdx = allRows.indexOf(currentRow);
				const targetRowIdx = event.key === 'ArrowDown' ? rowIdx + 1 : rowIdx - 1;

				if (targetRowIdx >= 0 && targetRowIdx < allRows.length) {
					const targetCells = Array.from(
						allRows[targetRowIdx].querySelectorAll('.flex-table-cell')
					);
					if (colIdx < targetCells.length) {
						targetInput = targetCells[colIdx].querySelector(
							'input[type="text"]:not(:disabled)'
						);
					}
				}
			}

			if (targetInput) {
				targetInput.focus();
				targetInput.select();
			}
		},
		onCellFocus(sizeName, length) {
			this.focusedSizeName = sizeName;
			this.focusedLength = length;
		},
		onCellBlur() {
			this.focusedSizeName = null;
			this.focusedLength = null;
		},
	},
	computed: {
		// Size x length grids were 7rem + 4rem cells, which made a 2x3 chart a 23rem postage stamp next to the
		// 38rem single-column chart. Wider cells plus the mini modal width as a floor keep them comparable.
		sizeColumnRem() {
			return 10;
		},
		lengthColumnRem() {
			return 6;
		},
		lengthGridWidthRem() {
			return this.sizeColumnRem + this.computedLengths.length * this.lengthColumnRem;
		},
		lengthModalWidthRem() {
			return Math.max(38, this.lengthGridWidthRem + 4);
		},
		computedLengths() {
			let arr = [];
			let hasSelectionWithNoLengths = this.sizeSelectionData.filter(
				(sizeRecord) => !("length" in sizeRecord)
			);

			if (hasSelectionWithNoLengths && hasSelectionWithNoLengths.length > 0) {
				arr.push("N/A");
			}

			for (let i = this.startLength; i <= this.endLength; i++) {
				arr.push(i);
			}

			return arr;
		},
		computedTotal() {
			let total = 0;
			let quantites = [];

			//loop through sizeData and grab each qauntity array
			this.sizeSelections.forEach((obj) => {
				if (obj.quantity) {
					quantites = [...quantites, obj.quantity];
				}
			});

			//loop through specialsizing and grab each qauntity
			if (this.specialSizes.length > 0) {
				this.specialSizes.forEach((obj) => {
					if (obj.quantity !== null) {
						quantites.push(obj.quantity);
					}
				});
			}

			//calculate total quantites
			total = quantites.filter((i) => i !== "").reduce((a, b) => parseInt(a) + parseInt(b), 0);

			return total;
		},
		totalClass() {
			const total = this.computedTotal;
			const max = parseInt(this.maxQuantity);
			if (total === max) return 'total-complete';
			if (total > max) return 'total-over';
			return 'total-progress';
		},
		activeSizes() {
			const selectedSizeIds = this.sizeSelectionData.map((item) => item.sizeId);

			return this.sizes
				.filter((size) => {
					return selectedSizeIds.includes(size.sizeId) || !size.isRetired;
				})
				.sort((a, b) => a.sequence - b.sequence);
		},
	},
	mounted() {
		if (this.sizeSelectionData.length > 0) {
			//strip reactivity from the passed in prop so changes dont delegate unless user saves
			this.sizeSelections = JSON.parse(JSON.stringify(this.sizeSelectionData));
		}

		if (this.specialSizing.length > 0) {
			//strip reactivity from the passed in prop so changes dont delegate unless user saves
			this.specialSizes = JSON.parse(JSON.stringify(this.specialSizing));
		}

		if (this.activeSizes.length > 0) {
			// TODO - this is a hack. Find a better way to do this.
			//strip reactivity from the passed in prop so changes dont delegate unless user saves
			this.sizeData = JSON.parse(JSON.stringify(this.activeSizes));

			//find the lowest and greatest length value if chart has lengths
			if (this.includeLengths) {
				let lengthsString;
				if (this.enableInputs) {
					//whem inputs are enabled we need to map the length string based on the passed in length objects
					lengthsString = this.activeSizes
						.map((obj) =>
							Array.isArray(obj.lengths)
								? obj.lengths
										.filter((l) => !l.isRetired || this.isLengthInSelection(l.sizeLengthId))
										.map((len) => len.length)
								: obj.lengths.map((len) => len.length)
						)
						.toString();
				} else {
					//when inputs are disabled we need to map the length string based on the passed in length strings
					lengthsString = this.activeSizes
						.map((obj) =>
							Array.isArray(obj.lengths)
								? obj.lengths.filter(
										(l) => !l.isRetired || this.isLengthInSelection(l.sizeLengthId)
								  )
								: obj.lengths
						)
						.toString();
				}
				// Make sure that we have a string to work with
				if (lengthsString.replaceAll(",", "") !== "") {
					const sortedLengths = lengthsString.match(/\d+/g).sort((a, b) => a - b);
					this.startLength = parseInt(sortedLengths[0]);
					this.endLength = parseInt(sortedLengths[sortedLengths.length - 1]);
				} else {
					console.log("no lengths", this.activeSizes);
				}
			}
		}

		// Focus the first available size input field when component is mounted
		// Only focus if inputs are enabled and there are no existing selections
		if (this.enableInputs && this.showSizingChart) {
			this.$nextTick(() => {
				// Find the first size input (YXS at first length or first available size without lengths)
				let firstInput;

				if (this.includeLengths) {
					// Look for the first input in the sizing chart
					const inputs = document.querySelectorAll('.sizing-chart input[type="text"]');
					if (inputs.length > 0) {
						firstInput = inputs[0];
					}
				} else {
					// For no-length version, find the first input
					const inputs = document.querySelectorAll('.flex-table-2 input[type="text"]');
					if (inputs.length > 0) {
						firstInput = inputs[0];
					}
				}

				// If we found an input and it's not already populated, focus it
				if (firstInput && !firstInput.value) {
					setTimeout(() => {
						firstInput.focus();
						// Place cursor at position 0 to allow immediate typing
						if (typeof firstInput.setSelectionRange === "function") {
							firstInput.setSelectionRange(0, 0);
						}
					}, 500);
				}
			});
		}
	},
};
</script>

<style lang="scss" scoped>
.v-overlay {
	z-index: 6 !important;

	::v-deep {
		.v-overlay__content {
			width: fit-content;
			max-width: 95%;
		}
	}

	&-relative {
		position: fixed;
		padding: 3rem 0;
		width: 100%;
	}
}

.sizing-modal {
	min-width: auto;
	width: auto;
	max-height: 90vh;
	display: flex;
	flex-direction: column;

	&-mini {
		width: 38rem;
	}

	.input-modal-content {
		flex: 1 1 auto;
		min-height: 0;
		overflow-y: auto;
		overflow-x: hidden;
		padding-top: 0;
	}
}

.sizing-chart {
	background-color: $color-white;
	display: flex;
	flex-direction: column;

	&-style-code {
		font-size: 1.4rem;
		font-weight: 600;
		margin-left: 0.5rem;
	}

	&-alert {
		background-color: #fce4ec;
		color: #c2262f;
		font-size: 1.3rem;
		font-weight: 600;
		padding: 0.6rem 1rem;
		border-left: 3px solid #c2262f;
		flex: 0 0 auto;
	}

	.length-header {
		position: sticky;
		top: 0;
		z-index: 12;
		background-color: $color-white;
		display: flex;
		flex-flow: row nowrap;

		&-spacer {
			width: 10rem;
			flex: 0 0 10rem;
		}

		&-label {
			flex: 0 0 auto;
			box-sizing: border-box;
			background-color: $color-gray-dark;
			color: $color-white;
			text-align: center;
			display: flex;
			align-items: center;
			justify-content: center;
			min-height: 3rem;
			border: 1px solid $color-gray-dark;
		}
	}

	.flex-table {
		border: none;

		//table-body
		&[role="table-body"] {
			flex: 1;

			//first row only
			.flex-table-row:first-child {
				.flex-table-cell {
					background-color: $color-gray;

					&:nth-child(1) {
						background-color: $color-gray-dark;
						color: $color-white;
					}
				}
			}

			// zebra striping on alternating data rows
			.flex-table-row:nth-child(even) {
				.flex-table-cell:first-child:not(.highlight-row) {
					background-color: #c8c8c8;
				}

				.flex-table-cell input:not(:focus) {
					background-color: #f5f5f5;
				}
			}
		}

		.flex-table-row {
			flex-flow: row nowrap;
			border: none;
			min-height: 4rem;
		}

		.flex-table-row:not(:last-child) {
			border-bottom: none;
		}

		//all other table cells
		.flex-table-cell {
			width: 6rem;
			flex: 0 0 6rem;
			box-sizing: border-box;
			justify-content: center;
			background-color: $color-gray-2;
			border: 1px solid $color-gray-dark;
			padding: 0;

			&:nth-child(1) {
				width: 10rem;
				flex: 0 0 10rem;
				background-color: $color-gray;
			}

			&.highlight-row,
			&.highlight-col {
				background-color: #42a5f5 !important;
				color: $color-white !important;
			}

			input {
				width: 100%;
				height: 100%;
				background-color: $color-white;
				font-size: 1.4rem;
				border: none;
				text-align: center;

				&:focus {
					outline: none;
					box-shadow: inset 0 0 0 2px #42a5f5;
					background-color: #e3f2fd;
				}
				&.standard-length {
					background-color: #def6fd;
				}
			}
		}
	}

	.flex-table-2 {
		border: none;

		//first row only (Size / Quantity headers)
		.flex-table-row:first-child {
			.flex-table-cell {
				background-color: $color-gray;

				&:nth-child(1) {
					background-color: $color-gray-dark;
					color: $color-white;
				}
			}
		}

		// zebra striping
		.flex-table-row:nth-child(even) {
			.flex-table-cell:first-child:not(.highlight-row) {
				background-color: #c8c8c8;
			}

			.flex-table-cell input:not(:focus) {
				background-color: #f5f5f5;
			}
		}

		.flex-table-row {
			flex-flow: row nowrap;
			border: none;
			min-height: 4rem;
		}

		.flex-table-row:not(:last-child) {
			border-bottom: none;
		}

		//all other table cells
		.flex-table-cell {
			width: 30%;
			justify-content: center;
			background-color: $color-gray-2;
			border: 1px solid $color-gray-dark;
			padding: 0;

			&:nth-child(1) {
				width: 70%;
				background-color: $color-gray;
			}

			input {
				width: 100%;
				height: 100%;
				background-color: $color-white;
				font-size: 1.4rem;
				border: none;
				text-align: center;

				&:focus {
					outline: none;
					box-shadow: inset 0 0 0 2px #42a5f5;
					background-color: #e3f2fd;
				}
			}
		}
	}

	&-footer {
		flex: 0 0 auto;
	}

	&-special {
		margin-top: 1.5rem;
		padding-top: 1rem;
		border-top: 2px solid $color-gray-dark;

		&-header {
			font-size: 1.4rem;
			font-weight: 700;
			padding: 0 0.8rem 0.5rem;
		}

		.flex-table-3 {
			border: none;
			width: 100%;

			//table-head
			&:first-of-type {
				.flex-table-row {
					background-color: $color-white;
					min-height: initial;
					margin-bottom: 0.5rem;
				}

				.flex-table-cell {
					padding: 0rem 0.8rem;
				}
			}

			//table-body
			&:last-of-type {
				.flex-table-row {
					border: none;
				}
			}

			.flex-table-cell {
				align-items: flex-start;

				&:nth-child(1) {
					width: 30%;
				}
				&:nth-child(2) {
					width: 30%;
				}
				&:nth-child(3) {
					width: 30%;
				}
				&:nth-child(4) {
					width: 10%;
					align-items: center;
				}
			}

			.form-input {
				margin: 0;
			}
		}

		.flex-table-4 {
			border: none;
			width: 100%;

			//table-head
			&:first-of-type {
				.flex-table-row {
					background-color: $color-white;
					min-height: initial;
					margin-bottom: 0.5rem;
				}

				.flex-table-cell {
					padding: 0rem 0.8rem;
				}
			}

			//table-body
			&:last-of-type {
				.flex-table-row {
					border: none;
				}
			}

			.flex-table-cell {
				align-items: flex-start;

				&:nth-child(1) {
					width: 45%;
				}
				&:nth-child(2) {
					width: 45%;
				}
				&:nth-child(3) {
					width: 10%;
					align-items: center;
				}
			}

			.form-input {
				margin: 0;
			}
		}
	}

	&-total {
		font-size: 1.4rem;
		margin: 0 2rem;
		font-weight: 700;

		span {
			margin-left: 1rem;
		}

		.total-progress {
			font-size: 1.8rem;
			font-weight: 700;
			color: $color-gray-dark;
		}

		.total-complete {
			font-size: 1.8rem;
			font-weight: 700;
			color: #4caf50;
		}

		.total-over {
			font-size: 1.8rem;
			font-weight: 700;
			color: #c2262f;
		}

		.total-required {
			font-weight: initial;
			color: gray;
		}
	}

	&-footer-actions {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		justify-content: center;
		gap: 0.5rem;

		.form-input {
			display: flex;
			flex-flow: row;
			align-items: center;
			margin: 0 0 0 1.5rem;

			label {
				width: initial;
				margin-right: 1rem;
				white-space: nowrap;
			}
		}
	}
}

.alert-fade-enter-active,
.alert-fade-leave-active {
	transition: opacity 0.3s, max-height 0.3s;
	max-height: 4rem;
	overflow: hidden;
}

.alert-fade-enter,
.alert-fade-leave-to {
	opacity: 0;
	max-height: 0;
}
</style>
